/**
 * 大屏显示器 - 数据大屏展示
 * 收货快递、发货快递、正在维修、超时订单 四大模块
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Tag } from 'antd';
import {
  InboxOutlined, SendOutlined, ToolOutlined,
  ClockCircleOutlined, ReloadOutlined, FullscreenOutlined,
  FullscreenExitOutlined, WifiOutlined,
} from '@ant-design/icons';
import { boardscreenApi } from '../services/api';
import { useSystemConfig } from '../config/system';
import './BigScreen.css';

const REFRESH_INTERVAL = 30; // 秒

// ==================== 状态映射 ====================
const repairStatusMap = {
  pending:   { text: '待接修', color: '#8c8c8c' },
  received:  { text: '已接修', color: '#1677ff' },
  quoting:   { text: '待付款', color: '#fa8c16' },
  paid:      { text: '已付款', color: '#13c2c2' },
  repairing: { text: '维修中', color: '#1677ff' },
  to_ship:   { text: '待发货', color: '#faad14' },
  shipped:   { text: '已发货', color: '#52c41a' },
  completed: { text: '已完成', color: '#52c41a' },
  cancelled: { text: '已取消', color: '#ff4d4f' },
};

const expressStatusMap = {
  '0':  { text: '暂无轨迹', color: '#8c8c8c' },
  '1':  { text: '已揽收',   color: '#1677ff' },
  '2':  { text: '运输中',   color: '#fa8c16' },
  '3':  { text: '派送中',   color: '#13c2c2' },
  '4':  { text: '已签收',   color: '#52c41a' },
  '5':  { text: '异常',     color: '#ff4d4f' },
  '6':  { text: '已退签',   color: '#ff4d4f' },
};

function getExpressStatusTag(status) {
  const item = expressStatusMap[status] || { text: status || '未知', color: '#8c8c8c' };
  return <Tag color={item.color}>{item.text}</Tag>;
}

function getOrderTypeTag(type) {
  const map = { repair: '维修', sales: '销售', return: '返修' };
  const colorMap = { repair: '#1677ff', sales: '#52c41a', return: '#fa8c16' };
  return <Tag color={colorMap[type] || '#8c8c8c'}>{map[type] || type}</Tag>;
}

function getOvertimeLevelStyle(level) {
  switch (level) {
    case 'red':    return { color: '#ff4d4f', glow: 'rgba(255,77,79,0.6)' };
    case 'orange': return { color: '#fa8c16', glow: 'rgba(250,140,22,0.6)' };
    case 'yellow': return { color: '#fadb14', glow: 'rgba(250,219,20,0.5)' };
    default:       return { color: '#8c8c8c', glow: 'rgba(140,140,140,0.3)' };
  }
}

// ==================== 数字滚动效果 ====================
function AnimatedNumber({ value }) {
  return <span className="animated-number">{value}</span>;
}

// ==================== 面板容器 ====================
function Panel({ title, icon, count, children, color, style }) {
  return (
    <div className="bigscreen-panel" style={style}>
      <div className="bigscreen-panel-header">
        <span className="bigscreen-panel-icon" style={{ color }}>{icon}</span>
        <span className="bigscreen-panel-title">{title}</span>
        <span className="bigscreen-panel-count" style={{ color }}>
          <AnimatedNumber value={count} />
        </span>
      </div>
      <div className="bigscreen-panel-body">
        {children}
      </div>
    </div>
  );
}

// ==================== 主组件 ====================
function BigScreen() {
  const sysConfig = useSystemConfig();
  const [data, setData] = useState({
    incomingExpress: [],
    outgoingExpress: [],
    repairing: [],
    overtime: [],
    updateTime: '',
  });
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const containerRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const res = await boardscreenApi.getData();
      if (res.code === 0 || res.code === 200) {
        setData(res.data);
      }
    } catch (err) {
      console.error('大屏数据加载失败:', err);
    } finally {
      setLoading(false);
      setCountdown(REFRESH_INTERVAL);
    }
  }, []);

  // 自动刷新
  useEffect(() => {
    loadData();

    timerRef.current = setInterval(() => {
      loadData();
    }, REFRESH_INTERVAL * 1000);

    countdownRef.current = setInterval(() => {
      setCountdown(prev => (prev <= 1 ? REFRESH_INTERVAL : prev - 1));
    }, 1000);

    return () => {
      clearInterval(timerRef.current);
      clearInterval(countdownRef.current);
    };
  }, [loadData]);

  // 全屏切换
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const {
    incomingExpress = [],
    outgoingExpress = [],
    repairing = [],
    overtime = [],
    updateTime,
  } = data;

  return (
    <div className="bigscreen-wrapper" ref={containerRef}>
      {/* 顶部标题栏 */}
      <div className="bigscreen-header">
        <div className="bigscreen-title-section">
          <h1 className="bigscreen-title">{sysConfig.companyShortName}信息技术 · {sysConfig.bigscreenTitle}</h1>
          <div className="bigscreen-subtitle">
            <WifiOutlined className="pulse-dot" />
            <span>实时数据</span>
            <span className="bigscreen-time">{updateTime}</span>
          </div>
        </div>
        <div className="bigscreen-actions">
          <span className="bigscreen-countdown">
            <ReloadOutlined spin={loading} /> {countdown}s 刷新
          </span>
          <button className="bigscreen-btn" onClick={loadData}>
            <ReloadOutlined /> 立即刷新
          </button>
          <button className="bigscreen-btn" onClick={toggleFullscreen}>
            {isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            {isFullscreen ? '退出全屏' : '全屏'}
          </button>
        </div>
      </div>

      {/* 四列面板 */}
      <div className="bigscreen-grid">
        {/* 列1：收货快递 */}
        <Panel
          title="收货快递"
          icon={<InboxOutlined />}
          count={incomingExpress.length}
          color="#1677ff"
        >
          <div className="bigscreen-table">
            <div className="bigscreen-table-header">
              <span className="col-type">类型</span>
              <span className="col-no">单号</span>
              <span className="col-company">快递公司</span>
              <span className="col-code">快递单号</span>
              <span className="col-status">状态</span>
            </div>
            <div className="bigscreen-table-body">
              {incomingExpress.length === 0 ? (
                <div className="bigscreen-empty">暂无收货快递</div>
              ) : (
                incomingExpress.map((item, idx) => (
                  <div
                    key={item.id || idx}
                    className="bigscreen-table-row"
                    style={{ animationDelay: `${idx * 0.05}s` }}
                  >
                    <span className="col-type">{getOrderTypeTag(item.order_type)}</span>
                    <span className="col-no" title={item.order_no}>{item.order_no || '-'}</span>
                    <span className="col-company">{item.express_company || '-'}</span>
                    <span className="col-code" title={item.express_no}>{item.express_no || '-'}</span>
                    <span className="col-status">{getExpressStatusTag(item.express_status)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </Panel>

        {/* 列2：发货快递 */}
        <Panel
          title="发货快递"
          icon={<SendOutlined />}
          count={outgoingExpress.length}
          color="#52c41a"
        >
          <div className="bigscreen-table">
            <div className="bigscreen-table-header">
              <span className="col-type">类型</span>
              <span className="col-no">单号</span>
              <span className="col-company">快递公司</span>
              <span className="col-code">快递单号</span>
              <span className="col-status">状态</span>
            </div>
            <div className="bigscreen-table-body">
              {outgoingExpress.length === 0 ? (
                <div className="bigscreen-empty">暂无发货快递</div>
              ) : (
                outgoingExpress.map((item, idx) => (
                  <div
                    key={item.id || idx}
                    className="bigscreen-table-row"
                    style={{ animationDelay: `${idx * 0.05}s` }}
                  >
                    <span className="col-type">{getOrderTypeTag(item.order_type)}</span>
                    <span className="col-no" title={item.order_no}>{item.order_no || '-'}</span>
                    <span className="col-company">{item.express_company || '-'}</span>
                    <span className="col-code" title={item.express_no}>{item.express_no || '-'}</span>
                    <span className="col-status">{getExpressStatusTag(item.express_status)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </Panel>

        {/* 列3：正在维修 */}
        <Panel
          title="正在维修"
          icon={<ToolOutlined />}
          count={repairing.length}
          color="#fa8c16"
        >
          <div className="bigscreen-table">
            <div className="bigscreen-table-header">
              <span className="col-no">单号</span>
              <span className="col-name">客户</span>
              <span className="col-status">状态</span>
              <span className="col-time">耗时</span>
            </div>
            <div className="bigscreen-table-body">
              {repairing.length === 0 ? (
                <div className="bigscreen-empty">暂无进行中的维修</div>
              ) : (
                repairing.map((item, idx) => (
                  <div
                    key={item.id}
                    className="bigscreen-table-row"
                    style={{ animationDelay: `${idx * 0.05}s` }}
                  >
                    <span className="col-no" title={item.repair_no}>{item.repair_no}</span>
                    <span className="col-name" title={item.customer_name}>{item.customer_name || '-'}</span>
                    <span className="col-status">
                      <Tag color={(repairStatusMap[item.status] || {}).color}>
                        {(repairStatusMap[item.status] || {}).text || item.status}
                      </Tag>
                    </span>
                    <span className="col-time">{formatHours(item.elapsed_hours)}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </Panel>

        {/* 列4：超时订单 */}
        <Panel
          title="超时订单"
          icon={<ClockCircleOutlined />}
          count={overtime.length}
          color={overtime.length > 5 ? '#ff4d4f' : '#fa8c16'}
        >
          <div className="bigscreen-table">
            <div className="bigscreen-table-header">
              <span className="col-no">单号</span>
              <span className="col-name">客户</span>
              <span className="col-status">状态</span>
              <span className="col-time">超时</span>
            </div>
            <div className="bigscreen-table-body">
              {overtime.length === 0 ? (
                <div className="bigscreen-empty">
                  <span style={{ color: '#52c41a', fontSize: 20 }}>&#10003;</span>
                  <span>全部订单正常</span>
                </div>
              ) : (
                overtime.map((item, idx) => {
                  const style = getOvertimeLevelStyle(item.overtime_level);
                  return (
                    <div
                      key={item.id}
                      className="bigscreen-table-row overtime-row"
                      style={{
                        animationDelay: `${idx * 0.08}s`,
                        borderLeftColor: style.color,
                      }}
                    >
                      <span className="col-no" title={item.repair_no}>
                        <span className="overtime-dot" style={{
                          background: style.color,
                          boxShadow: `0 0 6px ${style.glow}`,
                        }} />
                        {item.repair_no}
                      </span>
                      <span className="col-name" title={item.customer_name}>{item.customer_name || '-'}</span>
                      <span className="col-status">
                        <Tag color={(repairStatusMap[item.status] || {}).color}>
                          {(repairStatusMap[item.status] || {}).text || item.status}
                        </Tag>
                      </span>
                      <span className="col-time overtime-label" style={{
                        color: style.color,
                        textShadow: `0 0 8px ${style.glow}`,
                      }}>
                        {item.overtime_label}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Panel>
      </div>

      {/* 底部状态栏 */}
      <div className="bigscreen-footer">
        <span>{String(sysConfig?.companyName || '')} · 生产数据大屏</span>
        <span className="bigscreen-footer-dot" />
        <span>自动刷新间隔：{REFRESH_INTERVAL}秒</span>
        <span className="bigscreen-footer-dot" />
        <span>© 2026 嘉宇信息技术公司 | www.lyjiayu.top</span>
      </div>
    </div>
  );
}

// ==================== 工具函数 ====================
function formatHours(hours) {
  if (!hours && hours !== 0) return '-';
  const h = Number(hours);
  if (h < 1) return '<1h';
  if (h < 24) return `${Math.floor(h)}h`;
  const days = Math.floor(h / 24);
  const remain = h % 24;
  if (remain === 0) return `${days}d`;
  return `${days}d${remain}h`;
}

export default BigScreen;
